import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"

const FONT_URL = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"

const T = {
  es: {
    title: "Inicia sesión",
    subtitle: "Entra a tu cuenta de KORIVA Studio",
    emailLabel: "Correo electrónico",
    emailPlaceholder: "tu@correo.com",
    passLabel: "Contraseña",
    passPlaceholder: "Tu contraseña",
    button: "Iniciar sesión",
    loading: "Entrando...",
    errorInvalid: "Correo o contraseña incorrectos.",
    errorGeneric: "Algo salió mal. Intenta de nuevo.",
    forgot: "¿Olvidaste tu contraseña?",
    noAccount: "¿No tienes cuenta?",
    register: "Regístrate"
  },
  en: {
    title: "Sign in",
    subtitle: "Access your KORIVA Studio account",
    emailLabel: "Email",
    emailPlaceholder: "you@email.com",
    passLabel: "Password",
    passPlaceholder: "Your password",
    button: "Sign in",
    loading: "Signing in...",
    errorInvalid: "Incorrect email or password.",
    errorGeneric: "Something went wrong. Try again.",
    forgot: "Forgot your password?",
    noAccount: "Don't have an account?",
    register: "Sign up"
  }
}

export default function KorivaLogin() {
  const navigate = useNavigate()
  const [lang, setLang] = useState("es")
  const [email, setEmail] = useState("")
  const [showPw, setShowPw] = useState(false)
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const t = T[lang]

  async function handleLogin() {
    if (!email.trim() || !password.trim()) {
      setError(lang === "es" ? "Completa todos los campos." : "Fill in all fields.")
      return
    }
    setLoading(true)
    setError("")
    const { error: loginError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password
    })
    if (loginError) {
      setError(loginError.message.includes("Invalid") ? t.errorInvalid : t.errorGeneric)
      setLoading(false)
      return
    }
    navigate("/dashboard")
  }

  function onKeyDown(e) {
    if (e.key === "Enter") handleLogin()
  }

  const s = {
    page: {
      minHeight: "100vh",
      background: OFF,
      fontFamily: B,
      color: TEXT,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px"
    },
    card: {
      background: WHITE,
      borderRadius: "20px",
      border: `1px solid ${BORDER}`,
      boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
      padding: "40px",
      width: "100%",
      maxWidth: "420px"
    },
    logo: {
      fontFamily: H,
      fontWeight: 800,
      fontSize: "24px",
      color: V,
      letterSpacing: "-0.02em",
      marginBottom: "8px"
    },
    title: {
      fontFamily: H,
      fontWeight: 700,
      fontSize: "26px",
      margin: "0 0 6px 0",
      letterSpacing: "-0.02em"
    },
    subtitle: {
      fontSize: "15px",
      color: MUTED,
      margin: "0 0 28px 0"
    },
    label: {
      display: "block",
      fontFamily: H,
      fontWeight: 600,
      fontSize: "14px",
      marginBottom: "8px"
    },
    input: {
      width: "100%",
      boxSizing: "border-box",
      padding: "13px 16px",
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      fontSize: "15px",
      fontFamily: B,
      outline: "none",
      marginBottom: "18px",
      background: WHITE
    },
    button: {
      width: "100%",
      padding: "14px",
      borderRadius: "12px",
      border: "none",
      background: V,
      color: WHITE,
      fontFamily: H,
      fontWeight: 700,
      fontSize: "16px",
      cursor: loading ? "default" : "pointer",
      opacity: loading ? 0.7 : 1,
      marginTop: "6px"
    },
    error: {
      background: "#FEF2F2",
      color: "#DC2626",
      borderRadius: "10px",
      padding: "12px 14px",
      fontSize: "14px",
      marginBottom: "18px",
      border: "1px solid #FECACA"
    },
    footer: {
      textAlign: "center",
      fontSize: "14px",
      color: MUTED,
      marginTop: "22px"
    },
    link: {
      color: V,
      fontWeight: 600,
      cursor: "pointer",
      textDecoration: "none"
    },
    langSwitch: {
      display: "flex",
      gap: "6px",
      justifyContent: "flex-end",
      marginBottom: "20px"
    },
    langBtn: (active) => ({
      padding: "5px 12px",
      borderRadius: "8px",
      border: `1px solid ${active ? V : BORDER}`,
      background: active ? V_SOFT : WHITE,
      color: active ? V : MUTED,
      fontFamily: H,
      fontWeight: 600,
      fontSize: "13px",
      cursor: "pointer"
    })
  }

  return (
    <div style={s.page}>
      <link rel="stylesheet" href={FONT_URL} />
      <div style={s.card}>
        <div style={s.langSwitch}>
          <button style={s.langBtn(lang === "es")} onClick={() => setLang("es")}>ES</button>
          <button style={s.langBtn(lang === "en")} onClick={() => setLang("en")}>EN</button>
        </div>
        <div style={s.logo}>KORIVA Studio</div>
        <h1 style={s.title}>{t.title}</h1>
        <p style={s.subtitle}>{t.subtitle}</p>

        {error && <div style={s.error}>{error}</div>}

        <label style={s.label}>{t.emailLabel}</label>
        <input
          style={s.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t.emailPlaceholder}
          autoComplete="email"
        />

        <label style={s.label}>{t.passLabel}</label>
        <div style={{position:"relative",width:"100%"}}>
          <input
            style={{...s.input, paddingRight:44}}
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t.passPlaceholder}
            autoComplete="current-password"
          />
          <button type="button" onClick={()=>setShowPw(!showPw)} aria-label={showPw?"Ocultar":"Mostrar"} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",padding:0,cursor:"pointer",color:MUTED,display:"flex",alignItems:"center"}}>
                {showPw ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
        </div>

        <button style={s.button} onClick={handleLogin} disabled={loading}>
          {loading ? t.loading : t.button}
        </button>

        <div style={{...s.footer, marginTop: "16px"}}>
          <span style={s.link} onClick={() => navigate("/recuperar")}>{t.forgot}</span>
        </div>

        <div style={{...s.footer, marginTop: "10px"}}>
          {t.noAccount}{" "}
          <span style={s.link} onClick={() => navigate("/onboarding")}>{t.register}</span>
        </div>
      </div>
    </div>
  )
}
